import infoBtn from "assets/images/info_btn.svg";
import AccountAuditWindow from "components/Audit/AccountAuditWindow";
import FormField from "components/Forms/FormField";
import Status from "components/General/Status";
import SvgBtn from "components/General/SvgBtn";
import LoadingOverlay from "components/Loaders/LoadingOverlay";
import LicenceOptionMapper from "components/Mappers/LicenceOptionMapper";
import OrganisationOptionMapper from "components/Mappers/OrganisationOptionMapper";
import ModalComponent from "components/Modal/ModalComponent";
import {
  fetchHandleDeleteAccount,
  fetchHandleUpdateAccount,
} from "helpers/Account/AccountHelpers";
import {
  defaultAccountErrorState,
  defaultUpdateAccountRequest,
} from "helpers/constants/AccountConstants";
import { TokenContext } from "helpers/constants/Contexts";
import { getUploadToken, putData, readFile } from "helpers/Files/FileHelpers";
import { capitaliseString } from "helpers/GeneralHelpers";
import { IAccount } from "interfaces/Account/IAccount";
import { IAccountsState } from "interfaces/Account/IAccountsState";
import { IButtonProps } from "interfaces/General/IButtonProps";
import { IFile } from "interfaces/General/IFile";
import { ILicenceState } from "interfaces/Licence/ILicenseState";
import { IOrganisationsState } from "interfaces/Organisation/IOrganisationsState";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Form, Stack } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  accountsStateSelector,
  licensesStateSelector,
  organisationsStateSelector,
} from "redux/states/stateSelectors";
import { IUpdateAccountRequest } from "../../interfaces/Account/IUpdateAccountRequest";
import FormFooter from "../Footers/FormFooter";
import AlertMessage from "../Message/AlertMessage";
import styles from "./EditAccount.module.scss";
import ImageSelector from "./ImageSelector";

const EditAccountComponent = () => {
  //#region states
  const accessToken = useContext(TokenContext);
  const accountsState: IAccountsState = useSelector(accountsStateSelector);
  const licenceTypesState: ILicenceState = useSelector(licensesStateSelector);
  const organisationsState: IOrganisationsState = useSelector(
    organisationsStateSelector
  );
  //#endregion

  //#region constants
  const { accountId, orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handleSubmit } = useForm();
  //#endregion

  //#region useStates
  const [account, setAccount] = useState<IUpdateAccountRequest>(
    defaultUpdateAccountRequest
  );
  const [accountChanges, setAccountChanges] = useState<IUpdateAccountRequest>();
  const [baseAccount, setBaseAccount] = useState<IUpdateAccountRequest>();
  const [accountFromState, setAccountFromState] = useState<IAccount>();
  const [initials, setInitials] = useState<string>();
  const [imageToUpload, setImageToUpload] = useState<IFile | null>();
  const [validated, setValidated] = useState(false);
  const [errorState, setErrorState] = useState(defaultAccountErrorState);
  const [showAlert, setShowAlert] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState<string>();
  const [failure, setFailure] = useState(false);
  const [orgArchived, setOrgArchived] = useState(false);
  const [orgSuspended, setOrgSuspended] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] =
    useState("Saving changes...");
  //#endregion

  //#region memoized values
  const cancelUrl = useMemo(
    () => (orgId ? `/organisations/${orgId}` : "/organisations"),
    [orgId]
  );
  //#endregion

  //#region useEffects
  useEffect(() => {
    let acc = accountsState.accounts.find((a) => a.accountId === accountId);
    if (!acc) {
      navigate("/organisations");
      return;
    }
    setAccountFromState(acc);
    let formAcc: IUpdateAccountRequest = {
      email: acc.email,
      firstName: capitaliseString(acc.firstName),
      lastName: capitaliseString(acc.lastName),
      profilePictureUrl: acc["profilePictureUrl"],
      licenceTypeId: acc["licence"]["licenceTypeId"],
      licenceExpiry: acc["licence"]["nextInvoiceDate"],
      organisationId: acc["organisation"]?.["organisationId"],
      suspended: acc.suspended,
      archived: acc.archived,
    };
    setAccount(formAcc);
    setBaseAccount(formAcc);
    setInitials(
      `${Array.from(acc.firstName)[0]}${Array.from(acc?.lastName)[0]}`
    );
  }, [accountId, accountsState.accounts, navigate]);

  const validate = useCallback(() => {
    setErrorState((prev) => ({
      ...prev,
      firstName: account.firstName?.trim().length === 0,
      lastName: account.lastName?.trim().length === 0,
      licenceType: account.licenceTypeId?.trim().length === 0,
      licenseExpiry:
        account.licenceExpiry?.trim().length === 0 ||
        moment.utc(account.licenceExpiry).isBefore(moment.utc()),
    }));
  }, [account]);

  const validateOrg = useCallback(() => {
    const organisation = organisationsState.organisations.find(
      (org) => org.organisationId === account.organisationId
    );
    const orgStatus = {
      isSuspended: organisation?.suspended || false,
      isArchived: organisation?.archived || false,
    };

    setMessage(
      orgStatus.isSuspended || orgStatus.isArchived
        ? `The organisation this account belongs to is ${
            orgStatus.isArchived ? "archived" : "suspended"
          }`
        : ""
    );
    setOrgArchived(orgStatus.isArchived);
    setOrgSuspended(orgStatus.isSuspended);
    setShowMessage(orgStatus.isSuspended || orgStatus.isArchived);
    setFailure(orgStatus.isSuspended || orgStatus.isArchived);
  }, [account.organisationId, organisationsState.organisations]);

  useEffect(() => {
    validate();
    validateOrg();
    if (JSON.stringify(account) !== JSON.stringify(baseAccount)) {
      setChangesMade(true);
    } else {
      setChangesMade(false);
    }
  }, [account, baseAccount, validate, validateOrg]);

  useEffect(() => {
    if (validated) {
      setMessage("Please fill out the required fields.");
      setFailure(true);
      setShowMessage(true);
      window.scrollTo(0, 0);
    }
  }, [validated]);
  //#endregion

  //#region event handlers
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setAccount((prev) => ({ ...prev, [name]: value }));
    setAccountChanges((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCheckChange = useCallback((event) => {
    const { name, checked } = event.target;
    setAccount((prev) => ({ ...prev, [name]: checked }));
    setAccountChanges((prev) => ({ ...prev, [name]: checked }));
  }, []);

  const handleImageChange = useCallback((event) => {
    if (event.target.files && event.target.files[0]) {
      const file: IFile = {
        path: event.target.files[0],
        name: event.target.files[0].name,
      };
      const fileUrl = URL.createObjectURL(event.target.files[0]);
      setImageToUpload(file);
      setAccount((prev) => ({
        ...prev,
        profilePictureUrl: fileUrl,
      }));
      setAccountChanges((prev) => ({
        ...prev,
        profilePictureUrl: fileUrl,
      }));
    }
  }, []);

  const handleImageDelete = useCallback(() => {
    setAccount((prev) => ({
      ...prev,
      profilePictureUrl: "",
    }));
    setAccountChanges((prev) => ({
      ...prev,
      profilePictureUrl: "",
    }));
    setImageToUpload(null);
  }, []);
  //#endregion

  //#region submission handlers
  const onUpdateAccountSuccess = useCallback(() => {
    setIsSubmitting(false);
    navigate(cancelUrl);
  }, [navigate, cancelUrl]);

  const onUpdateAccountError = useCallback((message: string) => {
    setIsSubmitting(false);
    setMessage(message);
    setFailure(true);
    setShowMessage(true);
    window.scrollTo(0, 0);
  }, []);

  const sendDeleteAccountRequest = useCallback(async () => {
    setValidated(false);
    setIsSubmitting(true);
    setSubmissionMessage("Archiving account...");

    await dispatch(
      fetchHandleDeleteAccount(
        accessToken,
        accountId!,
        () => onUpdateAccountSuccess(),
        (message) => onUpdateAccountError(message)
      )
    );
  }, [
    accessToken,
    accountId,
    dispatch,
    onUpdateAccountSuccess,
    onUpdateAccountError,
  ]);

  const sendUpdateAccountRequest = useCallback(
    async (accountToUpdate: IUpdateAccountRequest) => {
      setValidated(false);
      setIsSubmitting(true);
      setSubmissionMessage("Updating account...");

      try {
        if (imageToUpload) {
          setSubmissionMessage("Uploading image...");
          const uploadUrl = await getUploadToken(
            accessToken,
            accountId!,
            imageToUpload.name
          );
          const fileData = await readFile(imageToUpload);
          await putData(fileData, uploadUrl);
          accountToUpdate.profilePictureUrl = uploadUrl.split("?")[0];
          setSubmissionMessage("Saving changes...");
        }

        dispatch(
          fetchHandleUpdateAccount(
            accessToken,
            accountToUpdate,
            accountId!,
            baseAccount!,
            () => onUpdateAccountSuccess(),
            (message) => onUpdateAccountError(message)
          )
        );
      } catch (e) {
        console.error(e);
        onUpdateAccountError("Failed to send the account update request.");
      }
    },
    [
      accessToken,
      accountId,
      baseAccount,
      dispatch,
      imageToUpload,
      onUpdateAccountError,
      onUpdateAccountSuccess,
    ]
  );

  const onSubmit = useCallback(() => {
    if (accountChanges) {
      if (accountChanges.archived) {
        sendDeleteAccountRequest();
      } else if (
        !errorState.firstName &&
        !errorState.lastName &&
        !errorState.licenceType &&
        !errorState.licenseExpiry &&
        !orgArchived
      ) {
        sendUpdateAccountRequest(accountChanges);
      } else if (!orgArchived) {
        setValidated(true);
      }
    }
  }, [
    accountChanges,
    errorState,
    orgArchived,
    sendDeleteAccountRequest,
    sendUpdateAccountRequest,
  ]);

  const onCancel = useCallback(() => {
    if (changesMade) {
      setShowAlert(true);
    } else {
      navigate(cancelUrl);
    }
  }, [changesMade, cancelUrl, navigate]);

  const handleClose = useCallback(() => {
    setShowAlert(false);
  }, []);
  //#endregion

  //#region button props
  const primaryBtn: IButtonProps = useMemo(
    () => ({
      text: "Save Changes",
      onClick: () => handleSubmit(onSubmit)(),
      disabled: !changesMade || isSubmitting,
    }),
    [changesMade, handleSubmit, onSubmit, isSubmitting]
  );

  const secondaryBtn: IButtonProps = useMemo(
    () => ({
      text: "Cancel",
      onClick: onCancel,
      disabled: isSubmitting,
    }),
    [onCancel, isSubmitting]
  );
  //#endregion

  // Extract commonly used disabled condition for form fields
  const isFormFieldDisabled = useMemo(
    () => isSubmitting || baseAccount?.archived || orgArchived,
    [isSubmitting, baseAccount, orgArchived]
  );

  return (
    <>
      <div className="form-page">
        <div className="vstack gap-4 col-md-5 mx-auto">
          <h3 className="text-heading-1">Edit Account</h3>
          {showMessage && (
            <AlertMessage
              success={!failure}
              title={failure ? "Error" : "Success"}
              message={message}
            />
          )}
          <Form
            noValidate={true}
            onSubmit={handleSubmit(onSubmit)}
            className={styles.form}
          >
            <Stack direction="vertical" gap={4}>
              <Form.Group className={styles.formGroup}>
                <Stack direction="horizontal" gap={4}>
                  <p className="text-label-2">
                    <span>Editing Account: </span>
                    <b>{account?.email}</b>
                  </p>
                </Stack>
              </Form.Group>

              <Form.Group className={styles.formGroup}>
                <Stack direction="horizontal" gap={4}>
                  <Form.Label data-variant="label-2-bold">Status:</Form.Label>
                  <Status
                    suspended={account.suspended || orgSuspended}
                    archived={account.archived}
                  />
                  <SvgBtn
                    color="black"
                    icon={infoBtn}
                    onClick={() => setShowAudit(true)}
                  />
                </Stack>
              </Form.Group>

              <Form.Group className={styles.formGroup}>
                <Stack direction="horizontal" className="gap-24">
                  <div className="flex gap-10 items-center">
                    <Form.Label data-variant="label-2-bold">Suspend</Form.Label>
                    <Form.Check
                      disabled={
                        baseAccount?.archived ||
                        orgSuspended ||
                        orgArchived ||
                        isSubmitting
                      }
                      type="switch"
                      name="suspended"
                      id="suspend-switch"
                      checked={account?.suspended || orgSuspended}
                      onChange={handleCheckChange}
                    />
                  </div>
                  <div className="flex gap-10 items-center">
                    <Form.Label data-variant="label-2-bold">Archive</Form.Label>
                    <Form.Check
                      type="switch"
                      name="archived"
                      id="archive-switch"
                      checked={account?.archived}
                      onChange={handleCheckChange}
                      disabled={orgArchived || isSubmitting}
                    />
                  </div>
                </Stack>
              </Form.Group>

              <div className="flex justify-between">
                <Form.Group className={styles.formGroup}>
                  <Form.Label data-variant="label-2-bold">
                    Account Image
                  </Form.Label>
                  <Form.Text className="text-muted">
                    Upload account image
                  </Form.Text>
                </Form.Group>
                <ImageSelector
                  imageUrl={account?.profilePictureUrl}
                  onChange={handleImageChange}
                  onDeleted={handleImageDelete}
                  altStr={initials}
                  disabled={
                    isSubmitting || baseAccount?.archived || orgArchived
                  }
                />
              </div>

              <FormField
                label="First Name"
                helpText="Enter the first name for this account"
                name="firstName"
                placeholder="John"
                value={account?.firstName || ""}
                error={errorState.firstName}
                disabled={isFormFieldDisabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              />

              <FormField
                label="Last Name"
                helpText="Enter the last name for this account"
                name="lastName"
                placeholder="Smith"
                value={account?.lastName || ""}
                error={errorState.lastName}
                disabled={isFormFieldDisabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              />

              <FormField
                label="Licence Type"
                helpText="Select the licence type for this account"
                name="licenceTypeId"
                error={errorState.licenceType}
                disabled={isFormFieldDisabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              >
                <Form.Select
                  size="lg"
                  name="licenceTypeId"
                  value={account?.licenceTypeId || ""}
                  onChange={handleChange}
                  disabled={isFormFieldDisabled}
                  isInvalid={errorState.licenceType && validated}
                  isValid={!errorState.licenceType && validated}
                >
                  <LicenceOptionMapper
                    licenceTypes={licenceTypesState.licences}
                  />
                </Form.Select>
              </FormField>

              <FormField
                label="Organisation"
                helpText="Select the organisation for this account"
                name="organisationId"
                disabled={isFormFieldDisabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              >
                <Form.Select
                  size="lg"
                  name="organisationId"
                  value={account?.organisationId || ""}
                  onChange={handleChange}
                  disabled={isFormFieldDisabled}
                  isValid={validated}
                >
                  <OrganisationOptionMapper
                    organisations={organisationsState.organisations}
                  />
                </Form.Select>
              </FormField>

              <FormField
                label="Licence Expiry Date"
                helpText="Enter the date you want this account to be available until"
                name="licenceExpiry"
                type="date"
                value={
                  account.licenceExpiry
                    ? moment(account.licenceExpiry).format("yyyy-MM-DD")
                    : ""
                }
                error={errorState.licenseExpiry}
                disabled={isFormFieldDisabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
                minDate={moment().format("YYYY-MM-DD")}
              />
            </Stack>
          </Form>
        </div>
      </div>
      <FormFooter primaryBtn={primaryBtn} secondaryBtn={secondaryBtn} />
      <ModalComponent
        actionHandler={() => navigate(cancelUrl)}
        handleClose={handleClose}
        displayModal={showAlert}
        action={"Yes"}
        modalTitle={"Cancel Account Changes"}
        modalBody={
          "Your unsaved changes will be lost are you sure you want to cancel?"
        }
      />
      <AccountAuditWindow
        account={accountFromState}
        show={showAudit}
        handleClose={() => setShowAudit(false)}
      />
      <LoadingOverlay isLoading={isSubmitting} message={submissionMessage} />
    </>
  );
};

export default EditAccountComponent;
